import React, { useEffect, useState } from 'react';
import { 
    Box, 
    Container, 
    Paper, 
    Typography, 
    Button, 
    Skeleton, 
    IconButton,
    Tooltip,
    Zoom,
    Fab
} from '@mui/material';
import { 
    ArrowBack as ArrowBackIcon, 
    Print as PrintIcon, 
    Shield as ShieldIcon,
    KeyboardArrowUp as UpIcon,
    MailOutline as MailIcon
} from '@mui/icons-material';
import axios from 'axios';
import { apiUrl } from '../../Config';

const PrivacyPolicy = () => {
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [showScrollTop, setShowScrollTop] = useState(false);

    useEffect(() => {
        const fetchPolicy = async () => {
            try {
                const response = await axios.get(`${apiUrl}/cms/type/search?userType=employer&cmsType=privacy_policy`);
                if (response.data?.success && response.data?.data?.content) {
                    setContent(response.data.data.content);
                } else {
                    setContent('<h3>No Privacy Policy Configured</h3><p>The administrator has not configured a privacy policy yet. Please check back later.</p>');
                }
            } catch (error) {
                console.error('Error fetching privacy policy:', error);
                setContent('<h3>Failed to Load</h3><p>We encountered an error loading the privacy policy. Please try again later.</p>');
            } finally {
                setLoading(false);
            }
        };

        fetchPolicy();

        const handleScroll = () => {
            if (window.scrollY > 300) {
                setShowScrollTop(true);
            } else {
                setShowScrollTop(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleBack = () => {
        if (window.history.length > 1) {
            window.history.back();
        } else {
            window.location.href = '/';
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <Box sx={{ 
            minHeight: '100vh', 
            backgroundColor: '#F8F9FD', 
            pb: 8,
            display: 'flex', 
            flexDirection: 'column'
        }}>
            {/* Header Bar */}
            <Box sx={{ 
                position: 'sticky', 
                top: 0, 
                zIndex: 1100, 
                backgroundColor: 'rgba(255, 255, 255, 0.8)', 
                backdropFilter: 'blur(12px)',
                borderBottom: '1px solid #E5E7EB',
                py: 1.5,
                px: { xs: 2, md: 4 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                '@media print': {
                    display: 'none'
                }
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Tooltip title="Go Back">
                        <IconButton 
                            onClick={handleBack}
                            sx={{ 
                                border: '1px solid #E5E7EB', 
                                '&:hover': { backgroundColor: '#F3F4F6', transform: 'translateX(-2px)' },
                                transition: 'all 0.2s'
                            }}
                        >
                            <ArrowBackIcon sx={{ color: '#374151', fontSize: '20px' }} />
                        </IconButton>
                    </Tooltip>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box component="span" sx={{ color: '#6B31C4' }}>RONO</Box> Jobs
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        onClick={handlePrint}
                        startIcon={<PrintIcon />}
                        sx={{
                            borderColor: '#E5E7EB',
                            color: '#374151',
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: '8px',
                            px: 2,
                            py: 0.8,
                            '&:hover': {
                                borderColor: '#6B31C4',
                                color: '#6B31C4',
                                backgroundColor: 'rgba(107, 49, 196, 0.04)'
                            }
                        }}
                    >
                        Print Policy
                    </Button>
                </Box>
            </Box>

            {/* Hero / Banner Area */}
            <Box sx={{ 
                background: 'linear-gradient(135deg, #6B31C4 0%, #4C1D95 100%)', 
                color: '#FFFFFF',
                py: { xs: 6, md: 8 },
                position: 'relative',
                overflow: 'hidden',
                mb: -6,
                '@media print': {
                    background: 'none',
                    color: '#000000',
                    py: 2,
                    mb: 0
                }
            }}>
                {/* Decorative background shapes */}
                <Box sx={{ 
                    position: 'absolute', 
                    top: '-50%', 
                    right: '-10%', 
                    width: '300px', 
                    height: '300px', 
                    borderRadius: '50%', 
                    background: 'rgba(255, 255, 255, 0.05)', 
                    filter: 'blur(40px)',
                    pointerEvents: 'none'
                }} />
                <Box sx={{ 
                    position: 'absolute', 
                    bottom: '-30%', 
                    left: '5%', 
                    width: '200px', 
                    height: '200px', 
                    borderRadius: '50%', 
                    background: 'rgba(255, 255, 255, 0.03)', 
                    filter: 'blur(30px)',
                    pointerEvents: 'none'
                }} />

                <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2, '@media print': { display: 'none' } }}>
                        <Box sx={{ 
                            p: 1.5, 
                            borderRadius: '12px', 
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(4px)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                        }}>
                            <ShieldIcon sx={{ fontSize: '28px', color: '#FFFFFF' }} />
                        </Box>
                        <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '2px', color: 'rgba(255, 255, 255, 0.8)' }}>
                            Legal & Compliance
                        </Typography>
                    </Box>
                    <Typography variant="h3" sx={{ 
                        fontWeight: 800, 
                        fontSize: { xs: '32px', md: '44px' }, 
                        mb: 2,
                        textShadow: '0 2px 10px rgba(0,0,0,0.1)'
                    }}>
                        Privacy Policy
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.85)', maxWidth: '600px', fontSize: '16px' }}>
                        We value your privacy and are committed to protecting your personal data. This document details how we collect, use, and safeguard your information.
                    </Typography>
                </Container>
            </Box>

            {/* Document Content */}
            <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
                <Paper 
                    elevation={0} 
                    sx={{ 
                        p: { xs: 4, md: 6 }, 
                        borderRadius: '20px', 
                        border: '1px solid #E5E7EB',
                        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.02)',
                        '@media print': {
                            border: 'none',
                            boxShadow: 'none',
                            p: 0,
                            borderRadius: 0
                        }
                    }}
                >
                    {loading ? (
                        <Box sx={{ py: 2 }}>
                            <Skeleton variant="text" sx={{ fontSize: '2.5rem', mb: 3, width: '60%' }} />
                            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '12px', mb: 4 }} />
                            <Skeleton variant="text" sx={{ fontSize: '1.2rem', mb: 1, width: '90%' }} />
                            <Skeleton variant="text" sx={{ fontSize: '1.2rem', mb: 1, width: '95%' }} />
                            <Skeleton variant="text" sx={{ fontSize: '1.2rem', mb: 1, width: '40%' }} />
                            <Skeleton variant="rectangular" height={150} sx={{ borderRadius: '12px', mt: 4 }} />
                        </Box>
                    ) : (
                        <Box sx={{ 
                            color: '#374151', 
                            lineHeight: 1.8, 
                            fontSize: '15px',
                            '& p': { mb: 3 },
                            '& h1, & h2, & h3, & h4, & h5, & h6': { 
                                mt: 4, 
                                mb: 2, 
                                fontWeight: 700, 
                                color: '#111827',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5
                            },
                            '& h1': { 
                                fontSize: '24px', 
                                borderLeft: '4px solid #6B31C4', 
                                pl: 1.5,
                                py: 0.5,
                                backgroundColor: 'rgba(107, 49, 196, 0.02)',
                                borderRadius: '0 8px 8px 0'
                            },
                            '& h2': { 
                                fontSize: '20px', 
                                borderLeft: '3px solid #6B31C4', 
                                pl: 1.5 
                            },
                            '& h3': { 
                                fontSize: '18px' 
                            },
                            '& ul, & ol': { 
                                pl: 3, 
                                mb: 3,
                                '& li': {
                                    mb: 1.5
                                }
                            },
                            '& strong': {
                                color: '#111827',
                                fontWeight: 650
                            },
                            '& a': {
                                color: '#6B31C4',
                                textDecoration: 'none',
                                fontWeight: 600,
                                borderBottom: '1px dashed #6B31C4',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    color: '#4C1D95',
                                    borderBottom: '1px solid #4C1D95'
                                }
                            },
                            '& table': {
                                width: '100%',
                                borderCollapse: 'collapse',
                                my: 4,
                                border: '1px solid #E5E7EB',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                '& th, & td': {
                                    border: '1px solid #E5E7EB',
                                    padding: '12px 16px',
                                    textAlign: 'left'
                                },
                                '& th': {
                                    backgroundColor: '#F9FAFB',
                                    color: '#111827',
                                    fontWeight: 600
                                },
                                '& tr:nth-of-type(even)': {
                                    backgroundColor: '#F9FAFB'
                                }
                            }
                        }}
                        dangerouslySetInnerHTML={{ __html: content }}
                        />
                    )}

                    {/* Footer Contact Info inside card */}
                    <Box sx={{ 
                        mt: 6, 
                        pt: 4, 
                        borderTop: '1px solid #E5E7EB', 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: 3,
                        '@media print': {
                            display: 'none'
                        }
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ 
                                p: 1, 
                                borderRadius: '50%', 
                                backgroundColor: 'rgba(107, 49, 196, 0.06)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <MailIcon sx={{ fontSize: '20px', color: '#6B31C4' }} />
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={{ color: '#6B7280', display: 'block' }}>Have questions about your data?</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>support@ronohub.com</Typography>
                            </Box>
                        </Box>
                        
                        <Button 
                            variant="contained" 
                            onClick={handleBack}
                            sx={{ 
                                backgroundColor: '#6B31C4', 
                                px: 4, 
                                py: 1.2, 
                                fontWeight: 600, 
                                borderRadius: '8px',
                                boxShadow: '0 4px 14px rgba(107, 49, 196, 0.2)',
                                textTransform: 'none', 
                                '&:hover': { 
                                    backgroundColor: '#5a27a8',
                                    boxShadow: '0 6px 20px rgba(107, 49, 196, 0.3)'
                                },
                                transition: 'all 0.2s'
                            }}
                        >
                            Return to Portal
                        </Button>
                    </Box>
                </Paper>
            </Container>

            {/* Scroll to Top FAB */}
            <Zoom in={showScrollTop}>
                <Fab 
                    size="medium" 
                    onClick={scrollToTop}
                    sx={{ 
                        position: 'fixed', 
                        bottom: 32, 
                        right: 32, 
                        backgroundColor: '#FFFFFF',
                        color: '#6B31C4',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        border: '1px solid #E5E7EB',
                        '&:hover': {
                            backgroundColor: '#F9FAFB',
                            transform: 'translateY(-2px)'
                        },
                        transition: 'all 0.2s',
                        '@media print': {
                            display: 'none'
                        }
                    }}
                >
                    <UpIcon />
                </Fab>
            </Zoom>
        </Box>
    );
};

export default PrivacyPolicy;
